<template>
  <v-app>
    <home-page-shell>
      <home-hero-section :slides="slides" :loading="isLoadingEssentials" />

      <home-brand-strip />

      <div class="home-content">
        <template v-for="section in homeSections">
          <home-ranking-section
            v-if="section.type === 'ranking'"
            :key="section.id"
            :section="section"
            :items="sectionState(section.id).items"
            :loading="sectionState(section.id).loading"
            :error="sectionState(section.id).error"
            @retry="loadSection"
          />

          <home-project-section
            v-else-if="['project', 'editorial', 'completed'].includes(section.type)"
            :key="section.id"
            :section="section"
            :items="sectionState(section.id).items"
            :loading="sectionState(section.id).loading"
            :error="sectionState(section.id).error"
            @retry="loadSection"
            @favorite-changed="syncFavoriteState"
          />

          <home-banner-section
            v-else-if="section.type === 'banner'"
            :key="section.id"
            :items="bannerItems(section.id)"
            :loading="section.id === 'banners-comerciais' ? false : isLoadingBanners"
            :error="section.id === 'banners-comerciais' ? false : bannersError"
            :commercial="section.id === 'banners-comerciais'"
            @retry="loadPromotionalBanners"
          />

          <home-author-section
            v-else-if="section.type === 'authors'"
            :key="section.id"
            :section="section"
            :items="sectionState(section.id).items"
            :loading="sectionState(section.id).loading"
            :error="sectionState(section.id).error"
            @retry="loadSection"
          />
        </template>
      </div>

      <zinnes-informations
        :total-creators="totalCreators"
        :total-stories="totalStories"
        :total-views="totalViews"
      />
    </home-page-shell>

    <div v-if="showCookieModal" class="home-cookie-banner" role="status">
      <div class="home-cookie-banner__container">
        <p>
          Nós usamos cookies e outras tecnologias semelhantes para melhorar a sua experiência em nossos serviços,
          personalizar publicidade e recomendar conteúdo de seu interesse. Ao utilizar nossos serviços, você está
          ciente dessa funcionalidade. Informamos ainda que atualizamos nosso
          <a @click="goToPrivacyPolicy">Aviso de Privacidade</a>.
          Conheça nosso <a @click="goToPrivacyPolicy">Portal da Privacidade</a>
          e veja o nosso novo Aviso.
        </p>
        <button type="button" @click="acceptCookies">Prosseguir</button>
      </div>
    </div>
  </v-app>
</template>

<script>
import "@/styles/home-page/index.css";

import HomePageShell from "@/components/home/layout/HomePageShell.vue";
import HomeBrandStrip from "@/components/home/layout/HomeBrandStrip.vue";
import HomeHeroSection from "@/components/home/sections/HomeHeroSection.vue";
import HomeRankingSection from "@/components/home/sections/HomeRankingSection.vue";
import HomeProjectSection from "@/components/home/sections/HomeProjectSection.vue";
import HomeBannerSection from "@/components/home/sections/HomeBannerSection.vue";
import HomeAuthorSection from "@/components/home/sections/HomeAuthorSection.vue";
import ZinnesInformations from "@/components/ZinnesInformations.vue";
import { getEssentials, getSection } from "@/server/homeServe";
import BannerService from "@/server/BannerService";
import { HOME_SECTIONS, HOME_SECTION_TYPES, loadableHomeSections } from "@/utils/homeSectionConfig";
import {
  normalizeAuthors,
  normalizeBanners,
  normalizeDominaram,
  normalizeHeroSlides,
  normalizeMonthlyBattle,
  normalizeProjects,
  normalizeRanking,
  pickArray,
} from "@/utils/homeProjectNormalizers";

function createSectionState() {
  return HOME_SECTIONS.reduce((states, section) => {
    if (section.sectionType) {
      states[section.id] = {
        items: [],
        loading: true,
        error: false,
      };
    }
    return states;
  }, {});
}

export default {
  name: "HomeView",
  components: {
    HomePageShell,
    HomeBrandStrip,
    HomeHeroSection,
    HomeRankingSection,
    HomeProjectSection,
    HomeBannerSection,
    HomeAuthorSection,
    ZinnesInformations,
  },
  data() {
    return {
      homeSections: HOME_SECTIONS,
      sectionStates: createSectionState(),
      slides: [],
      banners: [],
      commercialBanners: [],
      totalCreators: 0,
      totalStories: 0,
      totalViews: 0,
      isLoadingEssentials: true,
      isLoadingBanners: true,
      bannersError: false,
      bannerService: new BannerService(),
      showCookieModal: false,
    };
  },
  created() {
    this.loadHome();
    this.checkCookieConsent();
  },
  methods: {
    async loadHome() {
      this.loadEssentials();
      this.loadPromotionalBanners();
      loadableHomeSections().forEach(section => this.loadSection(section));
    },

    async loadEssentials() {
      this.isLoadingEssentials = true;

      try {
        const response = await getEssentials();
        const data = response?.data || response || {};
        this.slides = normalizeHeroSlides(data.banners || []);
        this.commercialBanners = normalizeBanners(data.commercial_banners || []);
        this.totalCreators = data.total_creators || 0;
        this.totalStories = data.total_stories || 0;
        this.totalViews = data.total_views || 0;
      } catch (error) {
        console.error("Erro ao carregar dados essenciais da home:", error);
        this.slides = [];
      } finally {
        this.isLoadingEssentials = false;
      }
    },

    async loadPromotionalBanners() {
      this.isLoadingBanners = true;
      this.bannersError = false;

      try {
        const response = await this.bannerService.list({});
        const items = Array.isArray(response) ? response : response?.data || [];
        this.banners = normalizeBanners(items);
      } catch (error) {
        console.error("Erro ao carregar banners promocionais:", error);
        this.banners = [];
        this.bannersError = true;
      } finally {
        this.isLoadingBanners = false;
      }
    },

    async loadSection(section) {
      if (!section?.sectionType) return;

      this.$set(this.sectionStates, section.id, {
        ...this.sectionState(section.id),
        loading: true,
        error: false,
      });

      try {
        const response = await getSection(section.sectionType);
        const rawItems = this.sectionResponseItems(response, section);
        const items = this.normalizeSectionItems(rawItems, section);

        this.$set(this.sectionStates, section.id, {
          items,
          loading: false,
          error: false,
        });
      } catch (error) {
        console.error(`Erro ao carregar seção ${section.id}:`, error);
        this.$set(this.sectionStates, section.id, {
          ...this.sectionState(section.id),
          loading: false,
          error: true,
        });
      }
    },

    sectionResponseItems(response, section) {
      let items = pickArray(response, section.responseKey);

      if (!items.length && section.fallbackKeys) {
        section.fallbackKeys.some(key => {
          items = pickArray(response, key);
          return items.length;
        });
      }

      return items;
    },

    normalizeSectionItems(items, section) {
      if (section.sectionType === HOME_SECTION_TYPES.DOMINARAM) {
        return normalizeDominaram(items, section);
      }

      if (section.sectionType === HOME_SECTION_TYPES.MONTHLY_BATTLE) {
        return normalizeMonthlyBattle(items, section);
      }

      if (section.type === "ranking") {
        return normalizeRanking(items, section);
      }

      if (section.type === "authors") {
        return normalizeAuthors(items);
      }

      return normalizeProjects(items, {
        completedOnly: section.completedOnly,
        preserveOrder: section.preserveOrder,
      });
    },

    sectionState(id) {
      return this.sectionStates[id] || {
        items: [],
        loading: false,
        error: false,
      };
    },

    bannerItems(sectionId) {
      if (sectionId === "banners-comerciais") {
        return this.commercialBanners;
      }

      if (sectionId === "banners-secondary") {
        return [...this.banners].reverse();
      }

      return this.banners;
    },

    syncFavoriteState({ projectId, isFavorite }) {
      Object.keys(this.sectionStates).forEach(sectionId => {
        const state = this.sectionState(sectionId);
        const items = state.items.map(item => (
          item.id === projectId
            ? { ...item, is_favorite: isFavorite }
            : item
        ));

        this.$set(this.sectionStates, sectionId, { ...state, items });
      });
    },

    checkCookieConsent() {
      const cookieConsent = localStorage.getItem("zinnes_cookie_consent");
      this.showCookieModal = cookieConsent !== "accepted";
    },

    acceptCookies() {
      localStorage.setItem("zinnes_cookie_consent", "accepted");
      this.showCookieModal = false;
    },

    goToPrivacyPolicy() {
      this.$router.push("/politica-privacidade");
    },
  },
};
</script>
